/**
 * The plain words for what happened to an item that left active consideration.
 * The database's `item_outcomes` (migrations 860, 869) decides which outcome an
 * item has; this turns a row of it into words. The app's item page and command
 * palette and the public API's `GET /v1/items/:id/fate` all use this one copy.
 *
 * The outcome words are the owner's, approved 2026-10-14 (migration 869): Merged
 * into, Split into, Replaced by, Duplicate of, Retired, Archived. A reframe is
 * not an outcome and has no word here.
 *
 * No imports: this file runs in the browser and in an edge function.
 */

export type ItemOutcomeWord =
  | 'merged into'
  | 'split into'
  | 'replaced by'
  | 'duplicate of'
  | 'retired'
  | 'archived';

const OUTCOME_LEAD: Record<ItemOutcomeWord, string> = {
  'merged into': 'Merged into',
  'split into': 'Split into',
  'replaced by': 'Replaced by',
  'duplicate of': 'Duplicate of',
  retired: 'Retired',
  archived: 'Archived',
};

/** Said in place of a byline when no record says who or when. */
export const NO_OUTCOME_RECORD = 'no record says who or when';

/** "Merged into" — the outcome words, capitalized. */
export function outcomeLead(outcome: ItemOutcomeWord): string {
  return OUTCOME_LEAD[outcome] ?? 'Archived';
}

/** "Merged into Checkout v2 (and 1 more)" — the outcome and what it points at, no date or actor. */
export function outcomeHeadline(o: {
  outcome: ItemOutcomeWord;
  pointerItemTitle: string | null;
  alsoCount: number;
}): string {
  const lead = outcomeLead(o.outcome);
  if (!o.pointerItemTitle) return lead;
  const more = o.alsoCount > 0 ? ` (and ${o.alsoCount} more)` : '';
  return `${lead} ${o.pointerItemTitle}${more}`;
}

/** "by Dana Lee on Oct 3, 2026" — or the parts that are known; '' when neither is. */
export function outcomeByline(at: Date | null, actorName: string | null): string {
  const parts: string[] = [];
  if (actorName) parts.push(`by ${actorName}`);
  if (at && !Number.isNaN(at.getTime())) {
    parts.push(`on ${new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', year: 'numeric' }).format(at)}`);
  }
  return parts.join(' ');
}

/** "Merged into Checkout v2 — by Dana Lee on Oct 3, 2026 · same ask twice": the whole line as text. */
export function outcomeLine(
  o: {
    outcome: ItemOutcomeWord;
    pointerItemTitle: string | null;
    alsoCount: number;
    reason: string | null;
    at: Date | null;
  },
  actorName: string | null,
): string {
  const headline = outcomeHeadline(o);
  const byline = outcomeByline(o.at, actorName);
  if (byline) return o.reason ? `${headline} — ${byline} · ${o.reason}` : `${headline} — ${byline}`;
  return o.reason ? `${headline} · ${o.reason}` : `${headline} — ${NO_OUTCOME_RECORD}`;
}
